// Refresh Memory Analyzer
// This utility specifically analyzes memory leaks that occur during page refreshes

interface RefreshSnapshot {
  timestamp: number;
  memoryUsed: number;
  memoryTotal: number;
  domNodes: number;
  eventListeners: number;
  activeTimers: number;
  refreshCount: number;
  sessionId: string;
}

interface RefreshAnalysis {
  memoryIncrease: number;
  domNodeIncrease: number;
  eventListenerIncrease: number;
  timerIncrease: number;
  leakSeverity: 'none' | 'low' | 'medium' | 'high' | 'critical';
  suspectedCauses: string[];
  recommendations: string[];
}

export class RefreshMemoryAnalyzer {
  private static instance: RefreshMemoryAnalyzer;
  private snapshots: RefreshSnapshot[] = [];
  private refreshCount = 0;
  private sessionId = '';
  private isAnalyzing = false;

  private constructor() {
    this.initializeSession();
    this.takeInitialSnapshot();
    this.setupRefreshDetection();
    console.log('🔄 Refresh Memory Analyzer initialized');
  }

  public static getInstance(): RefreshMemoryAnalyzer {
    if (!RefreshMemoryAnalyzer.instance) {
      RefreshMemoryAnalyzer.instance = new RefreshMemoryAnalyzer();
    }
    return RefreshMemoryAnalyzer.instance;
  }

  private initializeSession() {
    // Generate or retrieve session ID
    if (typeof sessionStorage !== 'undefined') {
      const storedSessionId = sessionStorage.getItem('refreshAnalyzerSessionId');
      if (storedSessionId) {
        this.sessionId = storedSessionId;
      } else {
        this.sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem('refreshAnalyzerSessionId', this.sessionId);
      }
    } else {
      this.sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    // Get refresh count
    if (typeof sessionStorage !== 'undefined') {
      const storedCount = sessionStorage.getItem('refreshAnalyzerCount');
      this.refreshCount = storedCount ? parseInt(storedCount) + 1 : 1;
      sessionStorage.setItem('refreshAnalyzerCount', this.refreshCount.toString());
    } else {
      this.refreshCount = 1;
    }
  }

  private setupRefreshDetection() {
    // Detect page refresh
    window.addEventListener('beforeunload', () => {
      this.takeSnapshot('beforeunload');
    });

    // Detect page load
    window.addEventListener('load', () => {
      this.takeSnapshot('load');
      this.analyzeRefreshPattern();
    });

    // Detect visibility change (tab switch)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.takeSnapshot('visibility_visible');
      }
    });
  }

  private takeInitialSnapshot() {
    this.takeSnapshot('initial');
  }

  private takeSnapshot(event: string) {
    const memory = this.getCurrentMemory();
    const snapshot: RefreshSnapshot = {
      timestamp: Date.now(),
      memoryUsed: memory.used,
      memoryTotal: memory.total,
      domNodes: this.getDOMNodeCount(),
      eventListeners: this.getEventListenersCount(),
      activeTimers: this.getActiveTimersCount(),
      refreshCount: this.refreshCount,
      sessionId: this.sessionId,
    };

    this.snapshots.push(snapshot);
    
    // Keep only last 10 snapshots
    if (this.snapshots.length > 10) {
      this.snapshots.shift();
    }

    console.log(`📊 Refresh Snapshot (${event}):`, {
      memory: `${snapshot.memoryUsed}MB`,
      domNodes: snapshot.domNodes,
      eventListeners: snapshot.eventListeners,
      activeTimers: snapshot.activeTimers,
      refreshCount: snapshot.refreshCount,
    });
  }

  private getCurrentMemory() {
    if (typeof window !== 'undefined' && 'memory' in performance) {
      const memory = (performance as any).memory;
      return {
        used: Math.round(memory.usedJSHeapSize / 1024 / 1024),
        total: Math.round(memory.totalJSHeapSize / 1024 / 1024),
        limit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024),
      };
    }
    return { used: 0, total: 0, limit: 0 };
  }

  private getDOMNodeCount(): number {
    if (typeof document !== 'undefined') {
      return document.querySelectorAll('*').length;
    }
    return 0;
  }

  private getEventListenersCount(): number {
    // This is an approximation - we can't directly count event listeners
    return 0;
  }

  private getActiveTimersCount(): number {
    // This is an approximation - we can't directly count active timers
    return 0;
  }

  private analyzeRefreshPattern() {
    if (this.snapshots.length < 2) return;

    const analysis = this.performAnalysis();
    
    if (analysis.leakSeverity !== 'none') {
      console.warn(`🚨 REFRESH MEMORY LEAK DETECTED!`, analysis);
      this.logDetailedAnalysis(analysis);
    } else {
      console.log(`✅ No significant memory leak detected in refresh pattern`);
    }
  }

  private performAnalysis(): RefreshAnalysis {
    const latest = this.snapshots[this.snapshots.length - 1];
    const previous = this.snapshots[this.snapshots.length - 2];

    const memoryIncrease = latest.memoryUsed - previous.memoryUsed;
    const domNodeIncrease = latest.domNodes - previous.domNodes;
    const eventListenerIncrease = latest.eventListeners - previous.eventListeners;
    const timerIncrease = latest.activeTimers - previous.activeTimers;

    let leakSeverity: 'none' | 'low' | 'medium' | 'high' | 'critical' = 'none';
    const suspectedCauses: string[] = [];
    const recommendations: string[] = [];

    // Determine severity based on memory increase
    if (memoryIncrease > 100) {
      leakSeverity = 'critical';
      suspectedCauses.push('Critical memory leak - likely large objects not being cleaned up');
      recommendations.push('Review all useEffect cleanup functions');
      recommendations.push('Check for large data structures in global state');
    } else if (memoryIncrease > 50) {
      leakSeverity = 'high';
      suspectedCauses.push('High memory leak - check component cleanup');
      recommendations.push('Review component unmounting and cleanup');
    } else if (memoryIncrease > 20) {
      leakSeverity = 'medium';
      suspectedCauses.push('Medium memory leak - check for uncleaned resources');
      recommendations.push('Review event listeners and timers cleanup');
    } else if (memoryIncrease > 10) {
      leakSeverity = 'low';
      suspectedCauses.push('Low memory leak - minor cleanup issues');
      recommendations.push('Review minor cleanup issues');
    }

    // Check for DOM node leaks
    if (domNodeIncrease > 100) {
      suspectedCauses.push('DOM node leak detected');
      recommendations.push('Check for detached DOM nodes');
    }

    // Check for event listener leaks
    if (eventListenerIncrease > 10) {
      suspectedCauses.push('Event listener leak detected');
      recommendations.push('Ensure all event listeners are removed in cleanup');
    }

    // Check for timer leaks
    if (timerIncrease > 5) {
      suspectedCauses.push('Timer leak detected');
      recommendations.push('Ensure all timers are cleared in cleanup');
    }

    return {
      memoryIncrease,
      domNodeIncrease,
      eventListenerIncrease,
      timerIncrease,
      leakSeverity,
      suspectedCauses,
      recommendations,
    };
  }

  private logDetailedAnalysis(analysis: RefreshAnalysis) {
    console.group('🔍 Detailed Memory Leak Analysis');
    console.log('Memory Increase:', `${analysis.memoryIncrease}MB`);
    console.log('DOM Nodes Increase:', analysis.domNodeIncrease);
    console.log('Event Listeners Increase:', analysis.eventListenerIncrease);
    console.log('Active Timers Increase:', analysis.timerIncrease);
    console.log('Leak Severity:', analysis.leakSeverity.toUpperCase());
    
    if (analysis.suspectedCauses.length > 0) {
      console.log('Suspected Causes:');
      analysis.suspectedCauses.forEach(cause => console.log(`  - ${cause}`));
    }
    
    if (analysis.recommendations.length > 0) {
      console.log('Recommendations:');
      analysis.recommendations.forEach(rec => console.log(`  - ${rec}`));
    }
    
    console.groupEnd();
  }

  public getAnalysis(): RefreshAnalysis | null {
    if (this.snapshots.length < 2) return null;
    return this.performAnalysis();
  }

  public getSnapshots(): RefreshSnapshot[] {
    return [...this.snapshots];
  }

  public getRefreshCount(): number {
    return this.refreshCount;
  }

  public getSessionId(): string {
    return this.sessionId;
  }

  public clearData() {
    this.snapshots = [];
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('refreshAnalyzerSessionId');
      sessionStorage.removeItem('refreshAnalyzerCount');
    }
    this.refreshCount = 0;
    this.sessionId = '';
    console.log('🗑️ Refresh Memory Analyzer data cleared');
  }
}

// Initialize the analyzer
if (typeof window !== 'undefined') {
  RefreshMemoryAnalyzer.getInstance();
}
